/**
 * Axios-based API client for AIP.
 *
 *  - Base URL `/api/v1` (the Vite dev server proxies this to the backend; in
 *    production set VITE_API_BASE_URL).
 *  - Attaches Authorization: Bearer <accessToken> from the auth store.
 *  - Single-flight refresh on 401: when the access token expires, the first
 *    failed request triggers a refresh; concurrent failed requests wait on
 *    the same promise and retry once the new token is available.
 *  - Sends an X-Request-Id we get back in the response (handy for support).
 *  - Normalizes the backend's `{error: {code,message,...}}` envelope to a
 *    plain `Error` with `code`, `status`, `requestId`, `details` properties.
 */
import axios from 'axios';
import { useAuthStore } from '../store/auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  timeout: 30000,
});

// ---- request ID helper (browser-native) ----
function genRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ---- Request: attach auth header + request id ----
api.interceptors.request.use((config) => {
  const { accessToken, activeClientId } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeClientId) {
    config.headers['X-Client-Id'] = activeClientId;
  }
  config.headers['X-Request-Id'] = config.headers['X-Request-Id'] || genRequestId();
  return config;
});

// ---- Response: error envelope normalization ----
function unwrapError(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  const requestId = err.response?.headers?.['x-request-id'];
  const env = data && typeof data === 'object' ? data.error : null;
  const e = new Error(env?.message || data?.detail || err.message || 'Request failed');
  e.code = env?.code || (status ? `HTTP_${status}` : 'NETWORK');
  e.status = status || 0;
  e.requestId = env?.request_id || requestId || null;
  e.details = env?.details || null;
  e.original = err;
  return e;
}

// ---- Single-flight refresh ----
let refreshPromise = null;

async function performRefresh() {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) {
    clear();
    throw new Error('No refresh token');
  }
  try {
    const r = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
    setTokens({
      accessToken: r.data.access_token,
      refreshToken: r.data.refresh_token,
      expiresIn: r.data.expires_in,
    });
    return r.data.access_token;
  } catch (e) {
    // Only nuke the session when the refresh token is genuinely invalid
    // (server explicitly returned 401). Network blips, 5xx, or 4xx other
    // than 401 leave the existing session intact so the user can retry —
    // a transient hiccup in the middle of submitting a long form should
    // never log them out.
    const status = e?.response?.status;
    if (status === 401) {
      clear();
    }
    throw e;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Skip refresh on the refresh endpoint itself or after we've already retried
    const isAuthEndpoint =
      original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');

    if (status === 401 && !isAuthEndpoint && !original?._retry) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        return Promise.reject(unwrapError(error));
      }
    }
    return Promise.reject(unwrapError(error));
  },
);

// ---- Convenience wrappers ----
export const apiGet = (url, config) => api.get(url, config).then((r) => r.data);
export const apiPost = (url, body, config) => api.post(url, body, config).then((r) => r.data);
export const apiPatch = (url, body, config) => api.patch(url, body, config).then((r) => r.data);
export const apiDelete = (url, config) => api.delete(url, config).then((r) => r.data);
